// Publish ▸ Author's co-authors (W9c; PROTOCOL.md §10.13): the project's byline, "A and B", kept by value in the project
// (`settings.byline`) so every device compiles the same. Only the owner changes it; a co-writer or commenter sees it.
import { useMemo, useState } from 'react';
import type { OpenedProject, Project } from '@writing-suite/core';
import { creditLines, CREDIT_ROLES, creditRoleName, OWN_AUTHOR, readByline, rolesOf, setByline, withRoles, type Byline, type CreditRole } from '@writing-suite/core/members';
import { announce } from '../shell/context.ts';
import type { LiveProject } from '../store/project.ts';

export function BylineEditor({ project, live, owner, ownerName }: { project: Project; live: LiveProject | null; owner: boolean; ownerName: string | null }) {
  const byline = useMemo(() => readByline((project as OpenedProject).record?.settings ?? {}), [project]);
  const [editing, setEditing] = useState(false);
  const [names, setNames] = useState('');
  const [joiner, setJoiner] = useState('and');
  // A musical's credits: what each person wrote (the published name under OWN_AUTHOR).
  const [roles, setRoles] = useState<Byline>(byline);
  const musical = project.musical;
  const [error, setError] = useState<string | null>(null);
  const start = () => {
    setNames(byline.names.join('\n'));
    setJoiner(byline.joiner);
    setRoles(byline);
    setError(null);
    setEditing(true);
  };
  const save = () => {
    if (!live) return;
    const list = names.split('\n').map((n) => n.trim()).filter((n) => n !== '');
    try {
      live.editDocuments((docs) => {
        const doc = docs.get('project');
        if (doc) docs.set('project', setByline(doc, { names: list, joiner, ...(roles.roles ? { roles: roles.roles } : {}) }, new Date()));
      });
      setEditing(false);
      announce(list.length ? 'Byline saved' : 'Co-authors removed');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  if (!owner) {
    return (
      <small className="owner-only" data-owner-only="byline">
        {byline.names.length ? `Co-authors: ${byline.names.join(', ')}. ` : ''}Only {ownerName || 'the project’s owner'} changes the byline.
      </small>
    );
  }
  if (!editing) {
    return (
      <small data-byline-names={byline.names.join('|')}>
        {byline.names.length ? `With ${byline.names.join(', ')}. ` : ''}
        <button type="button" className="link-button" onClick={start} disabled={!live?.writable}>
          {byline.names.length ? 'Change co-authors…' : 'Add co-authors…'}
        </button>
      </small>
    );
  }
  return (
    <div className="byline-editor" data-byline-editor>
      <label className="field-label stacked-label" htmlFor="byline-names">
        Co-authors, one a line, in byline order after you
      </label>
      <textarea id="byline-names" value={names} onChange={(e) => setNames(e.target.value)} rows={3} autoFocus />
      <label className="field-label stacked-label" htmlFor="byline-joiner">
        Joined with
      </label>
      <select id="byline-joiner" value={joiner} onChange={(e) => setJoiner(e.target.value)}>
        <option value="and">and</option>
        <option value={'&'}>&amp;</option>
      </select>
      <small>The contact block on a manuscript stays yours. Shunn’s header names every surname.</small>
      {musical ? (
        <fieldset className="byline-credits" data-byline-credits>
          <legend className="field-label">Credits</legend>
          {[[OWN_AUTHOR, ownerName || 'You'] as const, ...names.split('\n').map((n) => n.trim()).filter((n) => n !== '').map((n) => [n, n] as const)].map(([key, shown]) => (
            <div key={key} className="byline-credit-row">
              <span>{shown}</span>
              {CREDIT_ROLES.map((role: CreditRole) => (
                <label key={role}>
                  <input type="checkbox" checked={rolesOf(roles, key).includes(role)} onChange={(e) => {
                    const chosen = rolesOf(roles, key).filter((r) => r !== role);
                    if (e.target.checked) chosen.push(role);
                    setRoles(withRoles(roles, CREDIT_ROLES.filter((r) => chosen.includes(r)), key));
                  }} />
                  {creditRoleName(role)}
                </label>
              ))}
            </div>
          ))}
          <small data-credit-lines>
            {creditLines({ ...roles, names: names.split('\n').map((n) => n.trim()).filter((n) => n !== '') }, ownerName || 'You')?.join(' · ')
              ?? 'Choose who wrote the book, the music and the lyrics, and the title page credits each. With none chosen, it reads “by” and the names.'}
          </small>
        </fieldset>
      ) : null}
      {error ? <p className="error" role="alert">{error}</p> : null}
      <div className="actions">
        <button type="button" className="button secondary small" onClick={() => setEditing(false)}>
          Cancel
        </button>
        <button type="button" className="button primary small" onClick={save}>
          Save Byline
        </button>
      </div>
    </div>
  );
}
